import { forwardRef, useCallback, useState, InputHTMLAttributes, FocusEventHandler, useRef } from 'react';

import { Selection } from '@hh.ru/magritte-common-text-selection';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import {
    EyeOutlinedSize16,
    EyeCrossedOutlinedSize16,
    EyeOutlinedSize24,
    EyeCrossedOutlinedSize24,
} from '@hh.ru/magritte-ui-icon/icon';
import { Input } from '@hh.ru/magritte-ui-input/Input';
import { ControlledPasswordInputProps } from '@hh.ru/magritte-ui-input/types';

export const PasswordInput = forwardRef<
    HTMLInputElement,
    ControlledPasswordInputProps & Omit<InputHTMLAttributes<HTMLInputElement>, keyof ControlledPasswordInputProps>
>(({ onFocus, size, ...props }, ref) => {
    const [typePassword, setTypePassword] = useState(true);
    const clickHandler = useCallback(() => {
        setTypePassword((typePassword) => !typePassword);
    }, [setTypePassword]);

    const inputRef = useRef<HTMLInputElement>(null);
    const inputRefCallback = useMultipleRefs(ref, inputRef);

    const focusHandler = useCallback<FocusEventHandler<HTMLInputElement>>(
        (event) => {
            if (inputRef.current) {
                Selection.set(inputRef.current, 0, inputRef.current.value.length, 'forward');
            }
            onFocus && onFocus(event);
        },
        [onFocus, inputRef]
    );

    return (
        <Input
            {...props}
            size={size}
            buttonIcon={
                size === 'small'
                    ? (typePassword && EyeCrossedOutlinedSize16) || EyeOutlinedSize16
                    : (typePassword && EyeCrossedOutlinedSize24) || EyeOutlinedSize24
            }
            buttonDataQa="password-visibility-switch-button"
            onFocus={focusHandler}
            type={typePassword ? 'password' : 'text'}
            onButtonClick={clickHandler}
            ref={inputRefCallback}
        />
    );
});

PasswordInput.displayName = 'PasswordInput';
